const { parseEncodedObject, normalizePrice, normalizeCompareAtPrice } = window.ssUtils;

export class BaseCard {
  constructor(containerId) {
    this.container = document.getElementById(containerId);
  }

  clearContainer() {
    this.container.innerHTML = '';
  }

  formatPrice(price) {
    if (price === undefined || price === null) return '0.00';
    return String(price)?.split('.')?.[1]?.length === 1 ? String(price) + '0' : String(price);
  }

  getProductNameAndColor(name) {
    const [productName, productColor] = name.split(' - ');
    return {
      productName,
      productColor: productColor ? `<span class="title-color">${productColor}</span>` : ''
    };
  }

  formatImages(images, imageAlts, productName) {
    if (!images) return [];

    const imagesArr = images.split('|');
    const imageAltsArr = imageAlts || [];

    return imagesArr.map((image, index) => ({
      url: image.replace(/(\.[^.]+)$/, '_600x600$1'),
      alt: imageAltsArr[index] || productName || 'Product image'
    }));
  }

  createPriceSection(msrpPrice, price, productId, collections = []) {
    if (price === undefined || price === null) return '';
    let formattedPrice = '';

    if (Array.isArray(price)) {
      const priceArr = price.sort((a, b) => a - b);
      formattedPrice = [priceArr[0], priceArr[priceArr.length - 1]].map(p => `$${this.formatPrice(p)}`).join('-');
      // Remove only the first leading $ from formattedPrice if present
      formattedPrice = formattedPrice.replace(/^\$/, '');
    } else {
      formattedPrice = this.formatPrice(price);
    }

    const formattedMsrp = this.formatPrice(msrpPrice);

    if (!msrpPrice || Number(formattedMsrp) <= Number(formattedPrice)) {
      return `
        <div class="price__regular">
          <span class="visually-hidden visually-hidden--inline">Regular price</span>
          <span class="price-item price-item--regular" data-product-id="${productId}">
            $${formattedPrice} USD
          </span>
        </div>
      `;
    }

    return `
      <div class="price__sale">
        <span class="visually-hidden visually-hidden--inline">Regular price</span>
        <span>
          <span class="price-item price-item--regular" data-product-id="${productId}">
            $${formattedMsrp} USD
          </span>
        </span>
        <span class="visually-hidden visually-hidden--inline">Sale price</span>
        <span class="price-item price-item--sale price-item--last" data-product-id="${productId}">
          $${formattedPrice} USD
        </span>
      </div>
    `;
  }

  createCardBadge(product, variants) {
    const isSoldOut = product.ss_available === 0 || (variants?.length > 0 && variants?.every(variant => variant.inventory_quantity <= 0));

    const hasBadge = product.tags.some(tag => tag.includes('badge:'));

    if (hasBadge) {
      return `
        <span class="badge badge--bottom-left color-accent-3">
          ${product.tags.find(tag => tag.includes('badge:')).split(':')[1]}
        </span>
      `;
    }

    if (isSoldOut) {
      return `
        <span class="badge badge--bottom-left color-inverse">
          Sold Out
        </span>
      `;
    }

    if (product.ss_on_sale) {
      return `
        <span class="badge badge--bottom-left color-accent-2">
          Sale
        </span>
      `;
    }

    return '';
  }

  processVariants(variants) {
    if (!variants) return [];

    return variants.map(variant => ({
      ...variant,
      available: variant.inventory_quantity > 0
    }));
  }

  createSwatchBackground(colors) {
    if (!colors?.length) return '#FFFFFF';

    return `linear-gradient(45deg, ${colors.map((color, i) =>
      `${color} ${(i * 100) / colors.length}%, ${color} ${((i + 1) * 100) / colors.length}%`
    ).join(',')})`;
  }

  createSwatchElement(swatch, isHidden = false, isActive = false) {
    if (!swatch) return '';

    const backgroundColor = swatch.swatchImages
      ? `url(${swatch.swatchImages.value[0]})`
      : swatch.swatchColors
        ? this.createSwatchBackground(JSON.parse(swatch.swatchColors.value))
        : '#FFFFFF';

    const swatchProduct = {
      id: swatch.id.split('/').pop(),
      title: swatch.title,
      media: swatch.media.map(media => ({
        src: media.image?.medium || media.sources[0].url,
        alt: swatch.title
      })),
      variants: swatch.variants.map(variant => ({
        ...variant,
        id: variant.id.split('/').pop(),
        options: [variant.title]
      })),
      price: Number(swatch.variants?.[0]?.price?.amount) * 100,
      compare_at_price: Number(swatch.variants?.[0]?.compareAtPrice?.amount || swatch.variants?.[0]?.price?.amount) * 100,
      handle: swatch.handle,
    };

    return `
      <div
        class="product-color${isHidden ? ' swatch-hidden' : ''}${isActive ? ' active' : ''}"
        data-product='${JSON.stringify(swatchProduct)}'
        title="${swatch.title}"
        style="background: ${backgroundColor}"
      ></div>
    `;
  }

  createSwatchesSection(product) {
    if (!product.swatches?.length) return '';

    // Decode custom colors for current product (used if we need to synthesize a swatch)
    let customColors = product.mfield_custom_colors;
    if (Array.isArray(customColors)) {
      customColors = customColors.map(color => {
        if (typeof color === 'string') {
          let decoded = color.replace(/^"+|"+$/g, '').replace(/^'+|'+$/g, '');
          decoded = decoded.replace(/&quot;/g, '"');
          decoded = decoded.replace(/^"+|"+$/g, '').replace(/^'+|'+$/g, '');
          return decoded;
        }
        return color;
      });
    }

    const MAX_VISIBLE_SWATCHES = 5;
    const sortedSwatches = product.swatches
      .slice()
      .sort((a, b) => a.title.localeCompare(b.title));

    // Move the swatch that matches the current product to the front
    const activeIndexInSorted = sortedSwatches.findIndex(s => {
      const byHandle = s?.handle && product?.handle && s.handle === product.handle;
      return Boolean(byHandle);
    });
    const adjustedSwatches = sortedSwatches.slice();
    if (activeIndexInSorted > 0) {
      const [activeSwatch] = adjustedSwatches.splice(activeIndexInSorted, 1);
      adjustedSwatches.unshift(activeSwatch);
    } else if (activeIndexInSorted === -1) {
      // Synthesize a swatch representing the current product and place it first
      const imageUrls = (product.images || '').split('|').filter(Boolean);
      const primaryImage = imageUrls[0];
      const media = primaryImage ? [{ image: { medium: primaryImage }, sources: [{ url: primaryImage }] }] : [];

      const variants = parseEncodedObject(product.variants).map(variant => ({
        ...variant,
        // Preserve id as-is; downstream will handle both numeric and GID formats
        id: variant.id,
        // Ensure price objects exist with amount fields
        price: normalizePrice(variant.price),
        compareAtPrice: normalizeCompareAtPrice(variant),
      }));

      const currentSwatch = {
        id: product.id || product.uid || product.handle || product.name,
        title: product.name,
        handle: product.handle,
        media,
        variants,
      };
      if (Array.isArray(customColors) && customColors.length) {
        currentSwatch.swatchColors = { value: JSON.stringify(customColors) };
      }

      adjustedSwatches.unshift(currentSwatch);
    }
    const hasOverflow = adjustedSwatches.length > MAX_VISIBLE_SWATCHES;

    const swatchMarkup = adjustedSwatches
      .map((swatch, index) => this.createSwatchElement(
        swatch,
        index >= MAX_VISIBLE_SWATCHES,
        index === 0
      ))
      .join('');

    const moreButton = hasOverflow
      ? `<button type="button" class="product-color product-color--more" aria-label="Show more colors" title="Show more colors">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
            <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>`
      : '';

    return `
      <div class="product-color-options">
        <product-color-swatches class="product-color-swatches" data-max-visible="${MAX_VISIBLE_SWATCHES - 1}">
          ${swatchMarkup}
          ${moreButton}
        </product-color-swatches>
      </div>
    `;
  }
}